import React from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { InfoCard } from '@backstage/core-components';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import GitHubIcon from '@material-ui/icons/GitHub';
import SettingsIcon from '@material-ui/icons/Settings';
import LockIcon from '@material-ui/icons/Lock';
import CloudQueueIcon from '@material-ui/icons/CloudQueue';
import BuildIcon from '@material-ui/icons/Build';
import PaletteIcon from '@material-ui/icons/Palette';
import DescriptionIcon from '@material-ui/icons/Description';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

import { useEnv } from './EnvContext';
import { findApp, urlsForApp } from '../coalitions';

// Env-scoped Links card. Rows come from the coalition's URL builder;
// each row hides itself if the underlying template or app field is
// missing. Pipeline rows are driven by the coalition's pipelines[]
// array via the URL builder (BE / FE / WebsiteDeployment / whatever).
function iconForPipeline(pipeline: string): React.ReactNode {
  if (pipeline === 'FrontendDeployments') return <PaletteIcon />;
  return <BuildIcon />;
}

function labelForPipeline(pipeline: string): string {
  if (pipeline === 'BackendDeployments') return 'Jenkins — Backend';
  if (pipeline === 'FrontendDeployments') return 'Jenkins — Frontend';
  // Coalition-specific pipelines get "Jenkins — <humanised name>".
  return `Jenkins — ${pipeline}`;
}

export const EntitySparrowDeskLinksCard = () => {
  const { entity } = useEntity();
  const config = useApi(configApiRef);
  const { coalition, env } = useEnv(entity, config);

  const app = coalition ? findApp(coalition, entity) : undefined;
  if (!coalition || !app || !env) return null;

  const urls = urlsForApp(app, env, coalition);
  if (!urls) return null;

  const entries: Array<{
    icon: React.ReactNode;
    title: string;
    secondary: string;
    url: string;
    disabled?: boolean;
  }> = [];

  // Repo row(s):
  // - Backend + Frontend both present → two labelled rows.
  // - Backend only → one "Repository" row.
  // - Neither → single disabled "not applicable" row.
  //
  // Secondary text shows `owner/name`. For URL-driven coalitions
  // `coalition.configRepoOwner` is empty (no coalition-level owner);
  // fall back to the org portion of `github.com/project-slug` (app.slug),
  // otherwise strip a stray leading `/` if all we have is the bare name.
  const owner = coalition.configRepoOwner || app.slug?.split('/')[0] || '';
  const asRow = (name: string) => owner ? `${owner}/${name}` : name;
  const hasBackendRepo = Boolean(urls.repo && app.repo);
  const hasFrontendRepo = Boolean(urls.feRepo && app.feRepo);
  if (hasBackendRepo && hasFrontendRepo) {
    entries.push({
      icon: <GitHubIcon />,
      title: 'Repository — Backend',
      secondary: asRow(app.repo!),
      url: urls.repo,
    });
    entries.push({
      icon: <GitHubIcon />,
      title: 'Repository — Frontend',
      secondary: asRow(app.feRepo!),
      url: urls.feRepo,
    });
  } else if (hasBackendRepo) {
    entries.push({
      icon: <GitHubIcon />,
      title: 'Repository',
      secondary: asRow(app.repo!),
      url: urls.repo,
    });
  } else if (hasFrontendRepo) {
    entries.push({
      icon: <GitHubIcon />,
      title: 'Repository',
      secondary: asRow(app.feRepo!),
      url: urls.feRepo,
    });
  } else if (app.repo === undefined && app.feRepo === undefined) {
    entries.push({
      icon: <GitHubIcon />,
      title: 'Repository — not applicable',
      secondary: `${app.name} has no application code repo.`,
      url: '',
      disabled: true,
    });
  }

  if (urls.config) {
    entries.push({
      icon: <SettingsIcon />,
      title: 'Config',
      secondary: urls.config.replace(/^https?:\/\/(?:www\.)?/, ''),
      url: urls.config,
    });
  }

  if (urls.vault) {
    entries.push({
      icon: <LockIcon />,
      title: 'Vault',
      secondary: `${coalition.vaultMount} → ${urls.namespace}`,
      url: urls.vault,
    });
  } else if (app.hasVault === false) {
    entries.push({
      icon: <LockIcon />,
      title: 'Vault — not applicable',
      secondary: `${app.name} has no secrets in Vault.`,
      url: '',
      disabled: true,
    });
  }

  if (urls.argocd) {
    entries.push({
      icon: <CloudQueueIcon />,
      title: 'ArgoCD',
      secondary: `${urls.argocdAppName} (bundle)`,
      url: urls.argocd,
    });
  }

  if (urls.manifests) {
    entries.push({
      icon: <DescriptionIcon />,
      title: 'Kubernetes manifests',
      secondary: env,
      url: urls.manifests,
    });
  }

    urls.jenkins.forEach(j => {
    entries.push({
      icon: iconForPipeline(j.pipeline),
      title: labelForPipeline(j.pipeline),
      secondary: j.pipeline,
      url: j.url,
    });
  });

  return (
    <InfoCard title={`Environment tools — ${env}`}>
      <List dense>
        {entries.map(e =>
          e.disabled ? (
            <ListItem key={e.title} disabled>
              <ListItemIcon>{e.icon}</ListItemIcon>
              <ListItemText primary={e.title} secondary={e.secondary} />
            </ListItem>
          ) : (
            <ListItem
              key={e.title}
              button
              component="a"
              href={e.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ListItemIcon>{e.icon}</ListItemIcon>
              <ListItemText primary={e.title} secondary={e.secondary} />
              <OpenInNewIcon fontSize="small" color="action" />
            </ListItem>
          ),
        )}
      </List>
    </InfoCard>
  );
};

export default EntitySparrowDeskLinksCard;
